/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  formatFecha,
  Titulo,
  CampoInformacion,
  Formulario,
  urlBaseAPI,
  useEstetica,
} from "../../shared";
import logoVerticalBlanco from "../../images/logoVerticalBlanco.png";
import { Card } from "primereact/card";
import { Image } from "primereact/image";

const API_URL = urlBaseAPI();

// El fondo lo pinta App.js con el color principal del organismo, como el resto
// del portal público. Acá el <main> va transparente: solo centra el contenido
// y ocupa el alto de la ventana para empujar el footer hasta abajo.
const contenido = {
  padding: "1.5em",
  paddingBottom: "50px",
  marginLeft: "auto",
  marginRight: "auto",
  minHeight: "calc(100vh - var(--alturaFooter))",
};

// Página pública a la que apunta el QR de la etiqueta de ensayo
// (/E/<numero>). A propósito NO alcanza con la URL: pide además la clave de
// seguridad corta, impresa aparte en la etiqueta, así que adivinar/scrapear
// números de ensayo correlativos no sirve para ver nada.
export default function EnsayoPublico() {
  const { numero } = useParams();
  const estetica = useEstetica();
  const [verificando, setVerificando] = useState(false);
  const [error, setError] = useState(null);
  const [ensayo, setEnsayo] = useState(null);

  // Título de la pestaña: en la pantalla de la clave, solo el nombre del
  // organismo; ya con el ensayo cargado, "<Organismo> - Detalle de ensayo".
  // Pisa el título general que pone Estetica ("<Organismo> · Portal público").
  // El nombre del organismo lo resuelve el branding por dominio.
  useEffect(() => {
    if (!estetica?.nombre) return;
    document.title = ensayo
      ? `${estetica.nombre} - Detalle de ensayo`
      : estetica.nombre;
  }, [estetica?.nombre, ensayo]);

  function verificar(campos) {
    const clave = (campos.claveSeguridad || "").trim();
    if (!clave) return;
    setVerificando(true);
    setError(null);
    accessAPI(
      "POST",
      `E/${numero}/verificar`,
      { claveSeguridad: clave },
      (resp) => {
        setVerificando(false);
        setEnsayo(resp);
      },
      (resp) => {
        setVerificando(false);
        setError(resp?.[0]?.msg || "Número de ensayo o clave de seguridad incorrectos");
      },
    );
  }

  const claveFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "claveSeguridad",
            tipoitemformularioid: 1,
            etiqueta: "Clave de seguridad",
            requerido: true,
          },
        ],
      },
    ],
    botones: [
      {
        etiqueta: "Ver información",
        callback: verificar,
        validar: true,
      },
    ],
  };

  // El logo en blanco del organismo; si no cargó uno, el de la plataforma.
  // Mismo encabezado que login/recuperación de contraseña.
  const encabezadoLogo = (
    <div
      css={{
        paddingTop: "50px",
        marginBottom: "30px",
        width: "100%",
      }}
    >
      <img
        css={{
          width: "150px",
          marginLeft: "auto",
          marginRight: "auto",
          display: "block",
        }}
        src={estetica?.logos?.blanco || logoVerticalBlanco}
        alt="logo"
      />
    </div>
  );

  // Pantalla de ingreso de clave: logo del organismo y card centrada, como las
  // demás pantallas chicas del portal.
  if (!ensayo) {
    return (
      <main css={contenido}>
        {encabezadoLogo}
        <Card
          css={{
            width: "400px",
            maxWidth: "100%",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <Titulo>Ensayo {numero}</Titulo>
          <p css={{ color: "var(--grisTextos)", marginTop: 0 }}>
            Ingresá la clave de seguridad impresa en la etiqueta para ver la
            información de este ensayo.
          </p>
          {error && <p css={{ color: "var(--rojo)" }}>{error}</p>}
          <Formulario
            config={claveFormConfig}
            loader={verificando}
            formularioEnCuerpo
          />
        </Card>
      </main>
    );
  }

  const imagenes = ensayo.muestra?.imagenes || [];
  const productoCampos = ensayo.muestra?.productoCampos || [];
  const ensayosRelacionados = (ensayo.muestra?.ensayos || []).filter(
    (e) => e.id !== ensayo.id,
  );

  // Cards apiladas y centradas, como la muestra pública.
  return (
    <main css={[contenido, { maxWidth: "900px" }]}>
      {encabezadoLogo}

      <Card>
        <Titulo
          etiquetas={[
            ensayo.fechaResultado
              ? { texto: "Resultado entregado", ok: true }
              : { texto: "En laboratorio", alerta: true },
          ]}
        >
          Ensayo {ensayo.numero}
        </Titulo>
        <div className="grid">
          {ensayo.proceso?.cliente?.razonsocial && (
            <CampoInformacion nombreCampo="Cliente">
              {ensayo.proceso.cliente.razonsocial}
            </CampoInformacion>
          )}
          {ensayo.proceso && (
            <CampoInformacion nombreCampo="Proceso">
              {`${ensayo.proceso.nombre || ""}${
                ensayo.proceso.nombrefantasia ? ` - ${ensayo.proceso.nombrefantasia}` : ""
              }`}
            </CampoInformacion>
          )}
          {ensayo.muestra && (
            <CampoInformacion nombreCampo="Muestra">
              {ensayo.muestra.numero}
            </CampoInformacion>
          )}
          {ensayo.norma && (
            <CampoInformacion nombreCampo="Norma">
              {ensayo.norma.numero}
            </CampoInformacion>
          )}
          {ensayo.tipoEnsayo && (
            <CampoInformacion nombreCampo="Tipo de ensayo">
              {{ COMPLETO: "Completo", SEGUIMIENTO: "Seguimiento", ESPECIFICO: "Específico" }[
                ensayo.tipoEnsayo
              ] || ensayo.tipoEnsayo}
            </CampoInformacion>
          )}
          {ensayo.fechaCreacion && (
            <CampoInformacion nombreCampo="Enviado">
              {formatFecha(ensayo.fechaCreacion)}
            </CampoInformacion>
          )}
          {ensayo.fechaResultado && (
            <CampoInformacion nombreCampo="Resultado entregado">
              {formatFecha(ensayo.fechaResultado)}
            </CampoInformacion>
          )}
          {ensayo.notasEnsayo && (
            <CampoInformacion nombreCampo="Notas" anchoCompleto>
              {ensayo.notasEnsayo}
            </CampoInformacion>
          )}
        </div>
      </Card>

      {productoCampos.length > 0 && (
        <Card css={{ marginTop: "1.5rem" }}>
          <Titulo>Producto</Titulo>
          <div className="grid">
            {productoCampos.map((c, i) => (
              <CampoInformacion key={i} nombreCampo={c.etiqueta}>
                {c.valor || "—"}
              </CampoInformacion>
            ))}
          </div>
        </Card>
      )}

      {ensayo.tipoEnsayo !== "COMPLETO" &&
        Array.isArray(ensayo.puntosSeleccionados) &&
        ensayo.puntosSeleccionados.length > 0 && (
          <Card css={{ marginTop: "1.5rem" }}>
            <Titulo>Puntos a ensayar</Titulo>
            <ul css={{ margin: 0, paddingLeft: "1.2em" }}>
              {ensayo.puntosSeleccionados.map((p, i) => (
                <li key={i} css={{ marginBottom: "0.3em" }}>
                  {p.texto}
                </li>
              ))}
            </ul>
          </Card>
        )}

      {ensayosRelacionados.length > 0 && (
        <Card css={{ marginTop: "1.5rem" }}>
          <Titulo>Otros ensayos de esta muestra</Titulo>
          <div className="grid">
            {ensayosRelacionados.map((e) => (
              <CampoInformacion
                key={e.id}
                nombreCampo={`Ensayo ${e.numero}`}
                anchoCompleto
              >
                {e.norma?.numero || "—"}
                {e.fechaResultado
                  ? ` — resultado entregado ${formatFecha(e.fechaResultado)}`
                  : " — en laboratorio"}
              </CampoInformacion>
            ))}
          </div>
        </Card>
      )}

      {imagenes.length > 0 && (
        <Card css={{ marginTop: "1.5rem" }}>
          <Titulo>Imágenes</Titulo>
          <div
            css={{
              marginTop: "1rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {imagenes.map((img) => (
              <div
                key={img.archivo.hash}
                css={{ display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                <Image
                  src={`${API_URL}/archivo/${img.archivo.hash}/preview`}
                  alt={img.archivo.nombredescarga}
                  preview
                  imageStyle={{
                    width: "160px",
                    height: "120px",
                    objectFit: "cover",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            ))}
          </div>
        </Card>
      )}
    </main>
  );
}
